import Modal from './Modal';
import { useState } from 'react';
import { api } from '../hooks/useApi';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../hooks/AuthContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function DeleteAccountModal({ isOpen, onClose }: Props) {
  const { logout } = useAuth();
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canDelete = confirmText.trim().toLowerCase() === 'delete';

  const handleDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await api.post<{ success: boolean; message: string }>(
        '/api/auth/account/delete/'
      );
      if (!res.success) throw new Error(res as any);
      // Clear auth locally and redirect
      logout();
      onClose();
      window.location.href = '/';
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Account">
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 text-red-800">
          <AlertTriangle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">This will permanently delete your account.</p>
            <p className="text-sm mt-1">Your account will be deactivated. This action cannot be undone.</p>
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">Type <span className="font-semibold">delete</span> to confirm</label>
          <input
            type="text"
            className="w-full px-4 py-2 rounded-lg border-2 border-gray-200 focus:border-red-500 outline-none"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="delete"
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleDelete}
            disabled={!canDelete || deleting}
            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
          >
            {deleting ? 'Deleting...' : 'Delete Account'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
